// @flow

import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import DOMPurify from 'dompurify'
import { getI18n } from 'react-i18next'
import { find, propEq } from 'ramda'

import {
  getBrand,
  getBrandLanguageResources,
  getPromoCustomErrorLink,
} from '@alphaott/app-main/selectors'
import { prepareLanguageKey } from '@alphaott/common-utils/utils/languages'
import { PUBLIC_URL } from '@alphaott/app-config'

// eslint-disable-next-line no-unused-vars
export const useContentUnavailableHtml = (kind: string) => {
  const [html, setHtml] = useState(undefined)
  const [loading, setLoading] = useState(false)

  const i18n = getI18n()

  const brand = useSelector(getBrand)
  const brandLanguageResources = useSelector(getBrandLanguageResources)
  const activeLanguage = useMemo(
    () => find(propEq('key', prepareLanguageKey(i18n.language)), brandLanguageResources),
    [brandLanguageResources, i18n.language],
  )
  const promoCustomErrorLink = useSelector(getPromoCustomErrorLink(activeLanguage?.key))

  const prepareHtml = useCallback(
    (htmlDoc) => DOMPurify?.sanitize(htmlDoc, { ADD_ATTR: ['target'] }),
    [],
  )

  const handleFetch = useCallback(async (url) => {
    setLoading(true)

    try {
      const response = await fetch(url, { cache: 'no-store' })
      const preparedResponse = await response?.text()

      return preparedResponse
      // eslint-disable-next-line no-useless-catch
    } catch (err) {
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const loadDoc = useCallback(
    async (url) => {
      try {
        const response = await handleFetch(url)
        const preparedHtml = prepareHtml(response)

        setHtml(preparedHtml)
      } catch (err) {
        try {
          const preparedUrl = `${PUBLIC_URL}/brands/${brand?.id}/promo/content-unavailable.en.html`
          const response = await handleFetch(preparedUrl)
          const preparedHtml = prepareHtml(response)

          setHtml(preparedHtml)
        } catch (fallbackErr) {
          // eslint-disable-next-line no-console
          console.error(fallbackErr)
        }
      }
    },
    [brand?.id, handleFetch, prepareHtml],
  )

  useEffect(() => {
    if (promoCustomErrorLink) {
      loadDoc(promoCustomErrorLink)
    }
  }, [promoCustomErrorLink]) // eslint-disable-line react-hooks/exhaustive-deps

  return { loading, html }
}
